import { useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import { useAsync } from 'react-use';
import { FieldValidation } from '@rjsf/utils';
import { AIAzureOnboardingServiceApiRef, validateAzureOnboarding } from '../../services/AIAzureOnboarding.service';

export class NotOnboardedError extends Error {
  constructor(appShortname: string) {
    super(`Application ${appShortname} is not onboarded to Azure`);
    this.name = 'NotOnboardedError';
  }
}

export interface AzureOnboardingValidationData {
  isOnboarded: boolean;
  appShortname: string;
}

interface AppShortnameData {
  shortName?: string;
}

interface ScaffolderFormData {
  cmdbAppShortName?: AppShortnameData;
  [key: string]: any;
}

interface ScaffolderFormContext {
  formData?: ScaffolderFormData;
}

export const AppShortnameAzureOnboardingValidator = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<AzureOnboardingValidationData | null>) => {
  const azureOnboardingService = useApi(AIAzureOnboardingServiceApiRef);
  const typedFormContext = formContext as ScaffolderFormContext | undefined;
  const { shortName: appShortname = '' } = typedFormContext?.formData?.cmdbAppShortName || {};

  const state = useAsync(async () => {
    if (!appShortname) {
      return undefined;
    }

    const policy = await azureOnboardingService.fetchAzureOnboardingPolicy();
    const isOnboarded = validateAzureOnboarding(policy, appShortname);

    if (!isOnboarded) {
      throw new NotOnboardedError(appShortname);
    }

    return {
      isOnboarded: true,
      appShortname,
    };
  }, [appShortname, azureOnboardingService]);

  useEffect(() => {
    if (state.loading) {
      return;
    }

    if (state.error) {
      onChange(null);
    } else if (state.value !== undefined) {
      onChange(state.value);
    } else {
      onChange(null);
    }
  }, [state.loading, state.error, state.value, onChange]);

  if (!appShortname) {
    return (
      <Grid container spacing={2} style={{ marginTop: '16px', marginBottom: '16px' }}>
        <Grid item xs={12}>
          <Alert severity="info">Please select an App Shortname above to validate Azure onboarding status.</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} style={{ marginTop: '16px', marginBottom: '16px' }}>
      <Grid item xs={12}>
        {state.loading && (
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <CircularProgress size={20} aria-label="Loading Azure onboarding validation status" />
            </Grid>
            <Grid item>
              <Typography>Checking if {appShortname} is onboarded to Azure...</Typography>
            </Grid>
          </Grid>
        )}

        {!state.loading && state.error && state.error instanceof NotOnboardedError && (
          <Alert severity="error">
            Application Not Onboarded: {appShortname} has not yet been onboarded to Azure. Please ensure your
            application is onboarded to Azure as this is a prerequisite. You can refer to the documentation here for
            guidance on the onboarding process:{' '}
            <Link
              href="https://github.com/AAInternal/GaaS-Policy_as_Code/blob/main/runbooks/ONBOARDINGREQUESTRUNBOOOK.md"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              Azure Onboarding Runbook
            </Link>
            . If you have any questions, reach out to{' '}
            <Link
              href="mailto:DL_GaaS@aa.com"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              DL_GaaS@aa.com
            </Link>
            .
          </Alert>
        )}
        {!state.loading && state.error && !(state.error instanceof NotOnboardedError) && (
          <Alert severity="error">
            Unable to Verify Azure Onboarding: We encountered an issue while checking the Azure onboarding status for{' '}
            {appShortname}. This may be due to a temporary service issue or invalid data. Please try again later, or
            contact{' '}
            <Link
              href="mailto:DL_GaaS@aa.com"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              DL_GaaS@aa.com
            </Link>{' '}
            for assistance.
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};

export const appShortnameAzureOnboardingValidatorValidation = (
  value: AzureOnboardingValidationData | null,
  validation: FieldValidation,
) => {
  if (!value || !value.isOnboarded) {
    validation.addError('Application must be onboarded to Azure before proceeding');
  }
};
